import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useConfiguration } from '../../../context/configurationContext';
import { useIntl } from '../../../util/reactIntl';
import { isScrollingDisabled } from '../../../ducks/ui.duck';
import { loadData } from './MarketPlace.duck';
import { ASSET_NAME } from '../../../containers/LandingPage/LandingPage.duck';
import { camelize } from '../../../util/string';
import AdBanner from '../../AdBanner/AdBanner';
import styles from './MarketPlace.module.css';
import { BsSearch, BsGeoAlt, BsX } from 'react-icons/bs';
import Card from '../Components/Card/Card';
import LocationAutocompleteInput from '../../../components/LocationAutocompleteInput/LocationAutocompleteInput';
import { CategorySvg } from './Svg.map';
import classNames from 'classnames';

const MarketplacePagination = React.memo(({ pagination, onPageChange }) => {
  if (!pagination || pagination.totalPages <= 1) return null;
  const { page, totalPages } = pagination;
  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => onPageChange(i)}
          className={`${styles.pageButton} ${i === page ? styles.activePage : ''}`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };
  return (
    <div className={styles.pagination}>
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className={styles.paginationButton}
      >
        Previous
      </button>
      <div className={styles.pageNumbers}>{renderPageNumbers()}</div>
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className={styles.paginationButton}
      >
        Next
      </button>
    </div>
  );
});

MarketplacePagination.displayName = 'MarketplacePagination';

const CategoryDropdown = ({ category, isOpen, onToggle, onSelect, selectedPubCategoryLevel1 }) => {
  const hasSubcategories = category.subcategories && category.subcategories.length > 0;
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});
  useEffect(() => {
    if (isOpen && hasSubcategories) {
      const updatePosition = () => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          setDropdownStyle({
            top: `${rect.bottom + 4}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
          });
        }
      };
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, hasSubcategories]);
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = event => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        onToggle(category.id);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, category.id, onToggle]);

  const handleCategoryClick = () => {
    // if (hasSubcategories) {
    //   onToggle(category.id);
    // } else {
    // If no subcategories, select the category directly
    onSelect(null, category);
    // }
  };

  return (
    <div className={`${styles.categoryDropdown} ${isOpen ? styles.open : ''}`}>
      <button
        ref={buttonRef}
        className={`${styles.categoryMainButton} ${
          selectedPubCategoryLevel1 === category.id ? styles.activeCat : ''
        } `}
        onClick={handleCategoryClick}
      >
        <div className={styles.categoryContent}>
          {category.imageUrl && (
            <img
              src={category.imageUrl}
              alt={category.label || category.name}
              className={styles.categoryImage}
              onError={e => {
                e.target.style.display = 'none';
              }}
            />
          )}

          {CategorySvg[category.label || category.id]}
          <span className={styles.categoryLabel}>{category.label || category.name}</span>
        </div>
      </button>

      {/* {hasSubcategories && isOpen && (
        <div ref={dropdownRef} className={styles.subcategories} style={dropdownStyle}>
          {category.subcategories.map(subcategory => (
            <button
              key={subcategory.key || subcategory.id}
              className={styles.subcategoryButton}
              onClick={() => onSelect(subcategory, category)}
            >
              <span>{subcategory.label || subcategory.name}</span>
            </button>
          ))}
        </div>
      )} */}
    </div>
  );
};

// Search Bar Component
const SearchBarWithCategories = ({
  keywordInput,
  locationInput,
  onKeywordChange,
  onLocationChange,
  onKeyPress,
  onSearch,
}) => {
  const inputRef = useRef();
  const [locationState, setLocationState] = useState({
    search: locationInput || '',
    predictions: [],
    selectedPlace: null,
  });

  // Sync input value with prop
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.value = keywordInput || '';
    }
  }, [keywordInput]);

  const onSearchSubmit = () => {
    if (inputRef.current) {
      onSearch(inputRef.current.value);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      onSearchSubmit();
    }
  };

  // Update local state when locationInput prop changes
  useEffect(() => {
    // If we get a simple string from URL, convert it to the expected format
    if (typeof locationInput === 'string') {
      setLocationState({
        search: locationInput || '',
        predictions: [],
        selectedPlace: null,
      });
    }
  }, [locationInput]);

  // Handle location input change
  const handleLocationChange = value => {
    setLocationState(value);

    // REMOVED: Auto API trigger on change
    // Only update local state, wait for icon click
  };

  // Handle icon click to trigger search
  const handleIconClick = useCallback(
    e => {
      e.stopPropagation(); // Prevent bubbling issues
      if (locationState.selectedPlace && locationState.selectedPlace.address) {
        const bounds = locationState.selectedPlace.bounds;
        const address = locationState.selectedPlace.address;
        onLocationChange({ address, bounds });
      }
    },
    [locationState, onLocationChange]
  );

  // Custom icon component that is clickable
  const SearchIcon = useCallback(
    () => (
      <div
        onClick={handleIconClick}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title="Search Location"
      >
        <BsSearch />
      </div>
    ),
    [handleIconClick]
  );

  // Handle location key press
  const handleLocationKeyPress = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Only trigger search if a place is selected
      if (locationState.selectedPlace) {
        onKeyPress(e, 'location');
      }
    }
  };

  return (
    <div className={styles.searchContainerWithCategories}>
      <div className={styles.searchInputsSection}>
        {/* Keyword Search Field */}
        <div className={styles.searchWrapper}>
          <input
            ref={inputRef}
            className={styles.search}
            placeholder="Suche nach Artikeln..."
            defaultValue={keywordInput}
            onKeyPress={handleKeyPress}
          />
          &nbsp;
          <BsSearch
            className={styles.searchIcon}
            color="white"
            onClick={onSearchSubmit}
            style={{ cursor: 'pointer' }}
          />
        </div>
        {/* 
        <LocationAutocompleteInput
          inputRef={inputRef}
          placeholder="Standort eingeben..."
          rootClassName={styles.searchWrapper}
          iconClassName={styles.searchIcon}
          inputClassName={styles.search}
          predictionsClassName={styles.predictions}
          CustomIcon={SearchIcon}
          input={{
            name: 'location',
            value: locationState,
            onChange: handleLocationChange,
            onBlur: () => {
              // REMOVED: Auto API trigger on blur
              // Do nothing, wait for icon click
            },
            onKeyPress: handleLocationKeyPress,
            onFocus: () => {} // Add empty onFocus to satisfy PropTypes/usage if needed
          }}
          meta={{}}
          closeOnBlur={true}
        /> */}
      </div>
    </div>
  );
};

// Tracking/analytics params that should never be treated as search filters
const IGNORED_PARAMS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'fbclid',
  'gclid',
  'msclkid',
  'ttclid',
  'twclid',
  'li_fat_id',
  'mc_cid',
  'mc_eid',
  'igshid',
  'ref',
  'referrer',
]);

// Helper function to parse URL search params
const parseSearchParams = searchString => {
  const params = {};
  if (!searchString) return params;

  const searchParams = new URLSearchParams(searchString);
  for (const [key, value] of searchParams.entries()) {
    if (!IGNORED_PARAMS.has(key)) {
      params[key] = value;
    }
  }

  // Set page from URL or default to 1
  params.page = parseInt(params.page, 10) || 1;

  return params;
};

// Helper function to build URL search string
const buildSearchString = params => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.set(key, String(value));
    }
  });

  return searchParams.toString();
};

// Helper function to build API query string (INCLUDES ALL search params)
const buildAPIQueryString = params => {
  const searchParams = new URLSearchParams();

  // Include category parameters
  if (params.pub_categoryLevel1) {
    searchParams.set('pub_categoryLevel1', params.pub_categoryLevel1);
  }
  if (params.pub_categoryLevel2) {
    searchParams.set('pub_categoryLevel2', params.pub_categoryLevel2);
  }

  // Always include listing types
  if (params.pub_listingType) {
    searchParams.set('pub_listingType', params.pub_listingType);
  }

  // Include keyword search in API call
  if (params.keywords) {
    searchParams.set('keywords', params.keywords);
  }

  // Include bounds for location search in API call
  // Format: ne.lat,ne.lng,sw.lat,sw.lng
  if (params.bounds) {
    searchParams.set('bounds', params.bounds);
  }

  // Include page for pagination
  if (params.page) {
    searchParams.set('page', params.page);
  }

  return searchParams.toString();
};

const adBannerBlockMatchesCategory = (blockId = '', category = '') =>
  blockId
    .toLowerCase()
    .split('_')
    .includes(category);

const buildAdBannerItems = blocks =>
  blocks
    .map(block => {
      const media = block?.media || {};
      const blockId = block?.blockId || '';

      // Apply homepage condition for BOTH image and youtube
      const isForHomePage = blockId.includes('home-page');

      const isYoutube = media.fieldType === 'youtube' && media.youtubeVideoId;

      // Youtube banner
      if (isYoutube && isForHomePage) {
        return {
          type: 'youtube',
          youtubeVideoId: media.youtubeVideoId,
          aspectRatio: media.aspectRatio,
          blockId,
        };
      }

      // Image banner
      const variants = media?.image?.attributes?.variants || {};

      const url =
        variants.original800?.url ||
        variants.original400?.url ||
        variants.original1200?.url ||
        null;

      const href = media?.link?.href || block?.callToAction?.href || null;

      return url && isForHomePage
        ? {
            type: 'image',
            url,
            alt: media?.alt || block?.blockName || '',
            href,
            blockId,
          }
        : null;
    })
    .filter(Boolean);

// Helper function to shuffle an array (Fisher-Yates Shuffle)
const shuffleArray = array => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// Main Marketplace Component
function MarketplaceComponent(props) {
  const {
    pagination,
    searchInProgress,
    searchListingsError,
    dispatch,
    onLoadData,
    landingPageData,
    pageAssetsInProgress,
    pageAssetsError,
  } = props;
  const advertisementSection = landingPageData?.sections?.find(
    item => item.sectionId === 'advertisement-section'
  );
  const allAdBannerItems = useMemo(() => buildAdBannerItems(advertisementSection?.blocks || []), [
    advertisementSection?.blocks,
  ]);

  const config = useConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  // Get configuration values
  const categoryConfiguration = config?.categoryConfiguration;
  const listingCategories = categoryConfiguration?.categories || [];

  // State
  const [listings, setListings] = useState([]);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [activeCategory, setActiveCategory] = useState('alle');
  const [activeCategoryLabel, setActiveCategoryLabel] = useState('');
  const [activeSubcategoryLabel, setActiveSubcategoryLabel] = useState('');
  const [categoryImages, setCategoryImages] = useState({});
  const [fetchingImages, setFetchingImages] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [isLoadingListings, setIsLoadingListings] = useState(false);

  // Debounce timer references
  const keywordTimerRef = useRef(null);
  const locationTimerRef = useRef(null);

  // Parse URL params from location.search
  const urlQueryParams = useMemo(() => {
    return parseSearchParams(location.search);
  }, [location.search]);
  const activeAdBannerCategory = (
    urlQueryParams.pub_categoryLevel1 ||
    urlQueryParams.pub_categoryLevel2 ||
    ''
  ).toLowerCase();
  const adBannerItems = useMemo(
    () =>
      activeAdBannerCategory
        ? allAdBannerItems.filter(({ blockId }) =>
            adBannerBlockMatchesCategory(blockId, activeAdBannerCategory)
          )
        : allAdBannerItems,
    [activeAdBannerCategory, allAdBannerItems]
  );

  console.log('adBannerItems', adBannerItems);
  console.log('activeAdBannerCategory', activeAdBannerCategory);

  // Initialize input fields from URL params
  useEffect(() => {
    setKeywordInput(urlQueryParams.keywords || '');
    setLocationInput(urlQueryParams.address || '');

    // Find and set active category labels from URL params
    const categoryParam = urlQueryParams.pub_categoryLevel1;
    const subcategoryParam = urlQueryParams.pub_categoryLevel2;

    if (categoryParam && listingCategories.length > 0) {
      const category = listingCategories.find(c => c.key === categoryParam);
      if (category) {
        setActiveCategory(category.key || category.id);
        setActiveCategoryLabel(category.label || category.name);

        // Find subcategory if exists
        if (subcategoryParam && category.subcategories) {
          const subcategory = category.subcategories.find(s => s.key === subcategoryParam);
          if (subcategory) {
            setActiveSubcategoryLabel(subcategory.label || subcategory.name);
          } else {
            setActiveSubcategoryLabel('');
          }
        } else {
          setActiveSubcategoryLabel('');
        }
      }
    } else {
      setActiveCategory('alle');
      setActiveCategoryLabel('');
      setActiveSubcategoryLabel('');
    }
  }, [
    urlQueryParams.keywords,
    urlQueryParams.address,
    urlQueryParams.pub_categoryLevel1,
    urlQueryParams.pub_categoryLevel2,
    listingCategories,
  ]);

  // Enhance categories with images
  const enhancedCategories = useMemo(() => {
    if (!listingCategories || listingCategories.length === 0) {
      return [];
    }

    return listingCategories.map(category => ({
      id: category.key || category.id,
      key: category.key,
      label: category.label || category.name,
      imageUrl: categoryImages[category.key || category.id],
      subcategories: category.subcategories || [],
      dates: category.dates,
      keyword: category.keyword,
      priceMin: category.priceMin,
      priceMax: category.priceMax,
    }));
  }, [listingCategories, categoryImages]);

  // Function to update URL with new parameters (store UI state)
  const updateURLParams = useCallback(
    newParams => {
      // Build search string for URL
      const searchString = buildSearchString(newParams);
      const newPath = `${location.pathname}?${searchString}`;

      // Only update if the URL actually changed
      if (newPath !== `${location.pathname}${location.search}`) {
        history.replace(newPath);
      }
    },
    [history, location.pathname, location.search]
  );

  // Function to trigger search - sends ALL search params to loadData
  const triggerSearch = useCallback(async () => {
    // Build API query string with ALL search params
    const apiQueryString = buildAPIQueryString(urlQueryParams);
    const apiParams = parseSearchParams(apiQueryString);

    if (!config) {
      console.warn('Marketplace - Config is missing, skipping search');
      return;
    }

    if (!onLoadData) {
      console.error('Marketplace - onLoadData is not defined!');
      return;
    }

    // Set loading state
    setIsLoadingListings(true);

    try {
      // Call the Redux action via prop to trigger API with ALL search params
      const response = await onLoadData(apiParams, `?${apiQueryString}`, config);

      if (response?.payload?.data) {
        const extractedResponse = response.payload.data;
        const listing = extractedResponse?.data?.map(l => {
          const images = extractedResponse?.included?.filter(inc =>
            l.relationships?.images?.data?.map(i => i.id.uuid)?.includes(inc.id.uuid)
          );
          const author = extractedResponse?.included?.find(
            inc => l.relationships?.author?.data?.id?.uuid === inc.id.uuid
          );
          return {
            ...l,
            images: images,
            author: author,
          };
        });
        const isLandingPage = location.pathname === '/';
        const shuffledListings = (listing && isLandingPage) ? shuffleArray(listing) : (listing || []);
        setListings(shuffledListings);
      } else {
        console.warn(
          'Marketplace - loadData returned no data payload. Check access control or query.',
          response
        );
        // If response is undefined, it might be due to access control skipping the fetch
        setListings([]);
      }
    } catch (error) {
      console.error('Error loading listings:', error);
    } finally {
      setIsLoadingListings(false);
    }
  }, [urlQueryParams, config, onLoadData]);

  // Trigger search when URL params change
  useEffect(() => {
    triggerSearch();
  }, [
    urlQueryParams.pub_categoryLevel1,
    urlQueryParams.pub_categoryLevel2,
    urlQueryParams.keywords,
    urlQueryParams.address,
    urlQueryParams.page,
    triggerSearch,
  ]);

  // Event handlers
  const handleCategoryToggle = useCallback(categoryId => {
    setOpenDropdownId(prevId => (prevId === categoryId ? null : categoryId));
  }, []);

  const handleCategorySelect = useCallback(
    (subcategory, parentCategory) => {
      if (!parentCategory) return;

      // Update active category state
      setActiveCategory(parentCategory.id);
      setActiveCategoryLabel(parentCategory.label || parentCategory.name);
      setActiveSubcategoryLabel(subcategory ? subcategory.label || subcategory.name : '');
      setOpenDropdownId(null);

      // Build new parameters for URL (store UI state)
      const newParams = {
        page: 1,
        pub_categoryLevel1: parentCategory.key || parentCategory.id,
        // Only add subcategory if it exists
        ...(subcategory && { pub_categoryLevel2: subcategory.key || subcategory.id }),
        // Preserve other search parameters including bounds
        keywords: urlQueryParams.keywords || undefined,
        address: urlQueryParams.address || undefined,
        bounds: urlQueryParams.bounds || undefined,
      };

      updateURLParams(newParams);
    },
    [urlQueryParams, updateURLParams]
  );

  const handleAllCategories = useCallback(() => {
    setActiveCategory('alle');
    setActiveCategoryLabel('');
    setActiveSubcategoryLabel('');
    setOpenDropdownId(null);

    // Clear category filters but preserve keywords, address, and bounds
    const newParams = {
      page: 1,
      keywords: urlQueryParams.keywords || undefined,
      address: urlQueryParams.address || undefined,
      bounds: urlQueryParams.bounds || undefined,
    };

    updateURLParams(newParams);
  }, [urlQueryParams, updateURLParams]);

  const handleClearCategory = useCallback(() => {
    setActiveCategory('alle');
    setActiveCategoryLabel('');
    setActiveSubcategoryLabel('');

    // Remove category parameters from URL
    const newParams = {
      ...urlQueryParams,
      pub_categoryLevel1: undefined,
      pub_categoryLevel2: undefined,
      page: 1,
    };

    delete newParams.pub_categoryLevel1;
    delete newParams.pub_categoryLevel2;

    updateURLParams(newParams);
  }, [urlQueryParams, updateURLParams]);

  // Debounced keyword change handler - updates URL only
  const handleKeywordChange = useCallback(
    keyword => {
      setKeywordInput(keyword);

      // Clear existing timer
      if (keywordTimerRef.current) {
        clearTimeout(keywordTimerRef.current);
      }

      // Set new timer for debouncing
      keywordTimerRef.current = setTimeout(() => {
        const newParams = {
          ...urlQueryParams,
          keywords: keyword || undefined,
          page: 1,
        };

        // If keyword is empty, remove it from params
        if (!keyword) {
          delete newParams.keywords;
        }

        updateURLParams(newParams);
      }, 500);
    },
    [urlQueryParams, updateURLParams]
  );

  // Location change handler - updates URL only when place is selected
  const handleLocationChange = useCallback(
    locationValue => {
      // Handle new LocationAutocompleteInput format: { search, predictions, selectedPlace }
      if (locationValue && typeof locationValue === 'object') {
        // If it has selectedPlace, a location was picked from suggestions
        if (locationValue.selectedPlace) {
          const { address, bounds } = locationValue.selectedPlace;
          setLocationInput(address);

          // Clear existing timer
          if (locationTimerRef.current) {
            clearTimeout(locationTimerRef.current);
          }

          // Format bounds as: ne.lat,ne.lng,sw.lat,sw.lng
          let boundsString = '';
          if (bounds) {
            const { ne, sw } = bounds;
            boundsString = `${ne.lat},${ne.lng},${sw.lat},${sw.lng}`;
          }

          // Update URL with both address AND bounds (bounds visible in URL)
          const newParams = {
            ...urlQueryParams,
            address: address,
            bounds: boundsString || undefined,
            page: 1,
          };

          updateURLParams(newParams);
        }
        // If it has search property but no selectedPlace, user is just typing
        else if (locationValue.search !== undefined) {
          setLocationInput(locationValue.search);
          // Don't update URL, just update local state
        }
        // Old format: direct object with address and bounds
        else if (locationValue.address) {
          setLocationInput(locationValue.address);

          // Clear existing timer
          if (locationTimerRef.current) {
            clearTimeout(locationTimerRef.current);
          }

          // Format bounds as: ne.lat,ne.lng,sw.lat,sw.lng
          let boundsString = '';
          if (locationValue.bounds) {
            const { ne, sw } = locationValue.bounds;
            boundsString = `${ne.lat},${ne.lng},${sw.lat},${sw.lng}`;
          }

          // Update URL with both address AND bounds (bounds visible in URL)
          const newParams = {
            ...urlQueryParams,
            address: locationValue.address,
            bounds: boundsString || undefined,
            page: 1,
          };

          updateURLParams(newParams);
        }
      }
      // If it's just a string (typing), update input but don't update URL
      else if (typeof locationValue === 'string') {
        setLocationInput(locationValue);
      }
    },
    [urlQueryParams, updateURLParams]
  );

  // Handle immediate search on Enter key - updates URL only
  const handleKeyPress = useCallback(
    (e, type) => {
      if (e.key === 'Enter') {
        // Clear the timer to prevent duplicate calls
        if (type === 'keyword' && keywordTimerRef.current) {
          clearTimeout(keywordTimerRef.current);
        } else if (type === 'location' && locationTimerRef.current) {
          clearTimeout(locationTimerRef.current);
        }

        // Update URL immediately
        const newParams = { ...urlQueryParams };
        if (type === 'keyword') {
          newParams.keywords = keywordInput || undefined;
          if (!keywordInput) {
            delete newParams.keywords;
          }
        } else if (type === 'location') {
          newParams.address = locationInput || undefined;
          if (!locationInput) {
            delete newParams.address;
          }
        }
        newParams.page = 1;

        updateURLParams(newParams);
      }
    },
    [keywordInput, locationInput, urlQueryParams, updateURLParams]
  );

  const setActiveListing = useCallback(
    listingId => {
      history.push(`/l/${listingId}`);
    },
    [history]
  );

  const clearAllFilters = useCallback(() => {
    // Clear all parameters
    const newParams = {
      page: 1,
    };

    updateURLParams(newParams);

    // Reset UI state
    setActiveCategory('alle');
    setActiveCategoryLabel('');
    setActiveSubcategoryLabel('');
    setOpenDropdownId(null);
    setKeywordInput('');
    setLocationInput('');
  }, [updateURLParams]);

  // Handler to remove address filter (also removes bounds)
  const handleRemoveAddressFilter = useCallback(() => {
    const newParams = {
      ...urlQueryParams,
      address: undefined,
      bounds: undefined, // Remove bounds when address is removed
      page: 1,
    };

    delete newParams.address;
    delete newParams.bounds;

    updateURLParams(newParams);
    setLocationInput('');
  }, [urlQueryParams, updateURLParams]);

  const handlePageChange = useCallback(
    newPage => {
      const newParams = {
        ...urlQueryParams,
        page: newPage,
      };

      updateURLParams(newParams);
    },
    [urlQueryParams, updateURLParams]
  );

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (keywordTimerRef.current) {
        clearTimeout(keywordTimerRef.current);
      }
      if (locationTimerRef.current) {
        clearTimeout(locationTimerRef.current);
      }
    };
  }, []);

  // Memoize derived UI values
  const readableFilterValues = useMemo(() => {
    const activeFilters = [];

    Object.entries(urlQueryParams).forEach(([key, value]) => {
      if (key !== 'page' && value && key !== 'pub_listingType' && key !== 'bounds') {
        let displayValue = value;

        if (key === 'pub_categoryLevel1') {
          const category = enhancedCategories.find(c => c.key === value);
          displayValue = category?.label || value;
        } else if (key === 'pub_categoryLevel2') {
          for (const category of enhancedCategories) {
            const subcategory = category.subcategories?.find(sub => sub.key === value);
            if (subcategory) {
              displayValue = subcategory.label || value;
              break;
            }
          }
        } else if (key === 'keywords') {
          displayValue = `"${value}"`;
        } else if (key === 'address') {
          displayValue = `📍 ${value}`;
        }

        activeFilters.push({ key, value, displayValue });
      }
    });

    return activeFilters;
  }, [urlQueryParams, enhancedCategories]);

  const hasActiveFilters = readableFilterValues.length > 0;
  const hasListings = listings && listings.length > 0;
  const shouldShowLoading = searchInProgress || isLoadingListings;
  const shouldShowError = searchListingsError && !shouldShowLoading;
  const shouldShowNoResults = !shouldShowLoading && !shouldShowError && !hasListings;
  const shouldShowListings = !shouldShowLoading && !shouldShowError && hasListings;
  const selectedPubCategoryLevel1 = readableFilterValues?.find(
    item => item?.key === 'pub_categoryLevel1'
  )?.value;
  const keyValuePairsOfEnhancedCategories = Object?.fromEntries(
    enhancedCategories?.map(cat => [cat?.id, cat?.label])
  );
  const headerRef = useRef();
  // Scroll handler to manage header visibility
  useEffect(() => {
    let ticking = false;
    const shareTribeHeader = document.querySelector('.hideOnScroll');

    // Set initial transitions
    if (shareTribeHeader) {
      shareTribeHeader.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
    }

    const updateHeaderVisibility = () => {
      if (!headerRef.current) return;

      const isMobileView = window.matchMedia('(max-width: 768px)').matches;
      const scrollY = window.scrollY;

      if (isMobileView) {
        if (scrollY > 80) {
          // Show local header
          if (headerRef.current.style.opacity !== '1') {
            headerRef.current.style.display = 'block';
            headerRef.current.style.opacity = '1';
            headerRef.current.style.transform = 'translateY(-60px)';
            headerRef.current.style.pointerEvents = 'auto';

            // Hide ShareTribe header
            if (shareTribeHeader) {
              shareTribeHeader.style.opacity = '0';
              shareTribeHeader.style.pointerEvents = 'none';
            }
          }
        } else if (scrollY < 40) {
          // Hide local header
          if (headerRef.current.style.opacity !== '0') {
            headerRef.current.style.opacity = '0';
            headerRef.current.style.transform = 'translateY(-100%)';
            headerRef.current.style.pointerEvents = 'none';

            // Show ShareTribe header
            if (shareTribeHeader) {
              shareTribeHeader.style.opacity = '1';
              shareTribeHeader.style.pointerEvents = 'auto';
            }
          }
        }
      } else {
        // Desktop: always hide our header
        headerRef.current.style.opacity = '0';
        headerRef.current.style.transform = 'translateY(-150%)';
        headerRef.current.style.pointerEvents = 'none';

        // Show ShareTribe header
        if (shareTribeHeader) {
          shareTribeHeader.style.opacity = '1';
          shareTribeHeader.style.pointerEvents = 'auto';
        }
      }
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateHeaderVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    const onResize = () => {
      updateHeaderVisibility();
    };

    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);

    // Initial check
    updateHeaderVisibility();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);

      // Reset external header on unmount
      if (shareTribeHeader) {
        shareTribeHeader.style.display = '';
        shareTribeHeader.style.removeProperty('display');
      }
    };
  }, []);
  // Handle immediate search on submit
  const handleSearchSubmit = useCallback(
    keyword => {
      setKeywordInput(keyword);
      // Clear existing timer if any
      if (keywordTimerRef.current) {
        clearTimeout(keywordTimerRef.current);
      }

      const newParams = {
        ...urlQueryParams,
        keywords: keyword || undefined,
        page: 1,
      };

      if (!keyword) {
        delete newParams.keywords;
      }

      updateURLParams(newParams);
    },
    [urlQueryParams, updateURLParams]
  );

  return (
    <div className={styles.container}>
      <AdBanner images={adBannerItems} className={styles.adBannerMarketplace} />
      <header className={styles.topBar} ref={headerRef}>
        <SearchBarWithCategories
          keywordInput={keywordInput}
          locationInput={locationInput}
          onKeywordChange={handleKeywordChange}
          onLocationChange={handleLocationChange}
          onKeyPress={handleKeyPress}
          onSearch={handleSearchSubmit}
        />
        <div className={styles.categoriesSection}>
          {fetchingImages ? (
            <div className={styles.categoriesLoading}>
              <div className={styles.loadingSpinner}></div>
              <p>Lade Kategorien...</p>
            </div>
          ) : (
            <div className={styles.categoriesGrid}>
              <button
                className={`${styles.allCategoriesButton} ${
                  !selectedPubCategoryLevel1 ? styles.activeCat : ''
                }`}
                onClick={handleAllCategories}
              >
                {CategorySvg['Alle Kategorien']} Alle Kategorien
              </button>
              {enhancedCategories.map(category => (
                <CategoryDropdown
                  key={category.id}
                  category={category}
                  selectedPubCategoryLevel1={selectedPubCategoryLevel1}
                  isOpen={openDropdownId === category.id}
                  onToggle={handleCategoryToggle}
                  onSelect={subcategory => handleCategorySelect(subcategory, category)}
                />
              ))}
            </div>
          )}
        </div>
      </header>

      <div className={classNames(styles.categoriesSection, styles.categoriesSectionMobile)}>
        {fetchingImages ? (
          <div className={styles.categoriesLoading}>
            <div className={styles.loadingSpinner}></div>
            <p>Lade Kategorien...</p>
          </div>
        ) : (
          <div className={styles.categoriesGrid}>
            <button
              className={`${styles.allCategoriesButton} ${
                !selectedPubCategoryLevel1 ? styles.activeCat : ''
              }`}
              onClick={handleAllCategories}
            >
              {CategorySvg['Alle Kategorien']} Alle Kategorien
            </button>
            {enhancedCategories.map(category => (
              <CategoryDropdown
                key={category.id}
                category={category}
                selectedPubCategoryLevel1={selectedPubCategoryLevel1}
                isOpen={openDropdownId === category.id}
                onToggle={handleCategoryToggle}
                onSelect={subcategory => handleCategorySelect(subcategory, category)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className={styles.activeFilters}>
          <span className={styles.activeFiltersLabel}>Active Filters:</span>
          {readableFilterValues.map(({ key, displayValue }) => (
            <span key={key} className={styles.activeFilterTag}>
              {displayValue}
              <button
                className={styles.removeFilterButton}
                onClick={() => {
                  const newParams = { ...urlQueryParams };
                  delete newParams[key];

                  // If removing address, also remove bounds
                  if (key === 'address') {
                    delete newParams.bounds;
                    setLocationInput('');
                  }

                  // Update UI state
                  if (key === 'keywords') {
                    setKeywordInput('');
                  }

                  newParams.page = 1;
                  updateURLParams(newParams);
                }}
                title={`Remove ${key} filter`}
              >
                <BsX size={12} />
              </button>
            </span>
          ))}
          <button onClick={clearAllFilters} className={styles.clearFiltersButton}>
            Clear All
          </button>
        </div>
      )}

      {/* Loading indicator */}
      {shouldShowLoading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}></div>
          <p>Lade Suchergebnisse...</p>
        </div>
      )}
      {/* 
      {!shouldShowLoading && (
        <div className={styles.articleLength}>
          <number>{listings?.length || 0}</number> Artikel gefunden
        </div>
      )} */}
      {/* Error Display */}
      {shouldShowError && (
        <div className={styles.errorContainer}>
          <p>Fehler beim Laden der Suchergebnisse. Bitte versuchen Sie es erneut.</p>
        </div>
      )}

      {/* No Results */}
      {shouldShowNoResults && (
        <div className={styles.noResultsContainer}>
          <p>Keine Suchergebnisse gefunden. Bitte versuchen Sie eine andere Suche.</p>
        </div>
      )}

      {/* Listings Grid */}
      {shouldShowListings && (
        <>
          <div className={styles.articleLength}>Empfohlene Artikel</div>
          <div className={styles.listingsGrid}>
            {listings.map(listing => (
              <Card
                key={listing.id.uuid}
                listing={listing}
                onClick={setActiveListing}
                config={config}
                intl={intl}
                keyValuePairsOfEnhancedCategories={keyValuePairsOfEnhancedCategories}
              />
            ))}
          </div>

          {pagination && pagination.totalPages > 1 && (
            <MarketplacePagination pagination={pagination} onPageChange={handlePageChange} />
          )}
        </>
      )}
    </div>
  );
}

// Redux mapStateToProps
const mapStateToProps = state => {
  const { pagination, searchInProgress, searchListingsError } = state.MarketplacePage;
  const { pageAssetsData, inProgress: pageAssetsInProgress, error: pageAssetsError } =
    state.hostedAssets || {};
  const landingPageData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;

  return {
    pagination,
    searchInProgress,
    searchListingsError,
    scrollingDisabled: isScrollingDisabled(state),
    landingPageData,
    pageAssetsInProgress,
    pageAssetsError,
  };
};

// Redux mapDispatchToProps
const mapDispatchToProps = dispatch => ({
  onLoadData: (params, queryString, config) => dispatch(loadData(params, queryString, config)),
});

const ConnectedMarketplaceComponent = compose(connect(mapStateToProps, mapDispatchToProps))(
  MarketplaceComponent
);

// Enhanced component with context providers
const Marketplace = props => {
  const dispatch = useDispatch();
  return <ConnectedMarketplaceComponent {...props} dispatch={dispatch} />;
};

export default Marketplace;
